import React, { useEffect, useState } from 'react';
const storage: { [prop: string]: any } = {};
const possiblyParse = (input: string | null): undefined | unknown => {
    if (input !== null) {
        return JSON.parse(input);
    }
}

export interface userTokens {
    accessToken: { token: string, expiresIn: number };
    refreshTokens: string[];
    newToken?: true;
    refreshed?: true;
}

const dispatchStorageEvent = (key: string, newValue?: unknown, oldValue?: string) => {
    window.dispatchEvent(new StorageEvent("storage", { key, newValue: JSON.stringify(newValue), oldValue: oldValue }));
}

// Hook
const getItem = typeof window.localStorage !== 'undefined' ? (key: string) => possiblyParse(window.localStorage.getItem(key)) : (key: string) => {
    return storage[key] ? storage[key] : undefined
};
const setItem = typeof window.localStorage !== 'undefined' ? (key: string, value: unknown, withStorageUpdate = true) => {
    const oldValue = window.localStorage.getItem(key);
    window.localStorage.setItem(key, JSON.stringify(value));
    if (withStorageUpdate) dispatchStorageEvent(key, value, oldValue ?? undefined);
} : (key: string, value: unknown, withStorageUpdate = true) => {
    const oldValue = storage[key];
    storage[key] = value;
    if (withStorageUpdate) dispatchStorageEvent(key, value, oldValue);
};
const removeItem = typeof window.localStorage !== 'undefined' ? (key: string) => {
    const oldValue = window.localStorage.getItem(key);
    window.localStorage.removeItem(key)
    dispatchStorageEvent(key, undefined, oldValue ?? undefined);
} : (key: string) => { storage[key] = undefined; };

export const getCurrentUser = () => {
    const user = getItem("user") as userTokens;
    return (typeof user === "undefined" ? null : user) as userTokens | null;
}
export const setCurrentUser = (user: userTokens) => {
    return setItem("user", user);
}
export const logout = () => {
    removeItem("user");
}

export const useLocalStorage = <T>(key: string):
    [T | undefined, React.Dispatch<T>, React.Dispatch<void>] => {
    const [value, setValue] = useState<T | undefined>(getItem(key) as T | undefined);
    // Sync to localstorage
    useEffect(() => {
        if (typeof value !== "undefined")
            // Since this window already had a storage event dispatched, 
            // there's no need to dispatch another (and it would cause an infinite loop)
            setItem(key, value, false);
    }, [key, value]);

    useEffect(() => {
        // Sync from localstorage
        window.addEventListener("storage", e => {
            // If the value of localstorage changed
            if (e.key === key) {
                // Update the state of the item
                setValue(getItem(key) as T);
            }
        });
    }, [key]);

    return [value, setValue, () => { setValue(undefined); removeItem(key); }];
}